import { Base, FilterIcon, MagnifyingGlassIcon } from '@itsrever/design-system'
import { type ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { useChecklistLabels } from '@/ui/shared/checklist'
import {
  FILTER_KEYS,
  useProcessFilters,
  type FilterKey
} from './helpers/use-process-filters'

const {
  Badge,
  Button,
  Checklist,
  Input,
  Spinner,
  PopoverRoot,
  PopoverTrigger,
  PopoverPortal,
  PopoverPositioner,
  PopoverPopup
} = Base

const useActiveFilterCount = () => {
  const [searchParams] = useSearchParams()
  return FILTER_KEYS.reduce((n, k) => n + searchParams.getAll(k).length, 0)
}

export const FiltersPanel = () => {
  const { t } = useTranslation()
  const activeCount = useActiveFilterCount()

  return (
    <PopoverRoot>
      <PopoverTrigger
        render={
          <Button variant="secondary" className="active:scale-100">
            <FilterIcon />
            {t('orders.filters_title')}
            {activeCount > 0 && <Badge variant="count">{activeCount}</Badge>}
          </Button>
        }
      />
      <PopoverPortal>
        <PopoverPositioner sideOffset={8} align="start">
          {/* eslint-disable-next-line tailwindcss/migration-from-tailwind-2 */}
          <PopoverPopup className="max-h-96 w-80 overflow-clip p-0">
            <FiltersContent />
          </PopoverPopup>
        </PopoverPositioner>
      </PopoverPortal>
    </PopoverRoot>
  )
}

const FiltersContent = () => {
  const { t } = useTranslation()
  const checklistLabels = useChecklistLabels()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeCount = useActiveFilterCount()

  const [search, setSearch] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<FilterKey | null>(null)

  const filters = useProcessFilters()

  const activeFilter = filters.find((f) => f.key === selectedFilter) ?? null

  const handleBack = () => {
    setSelectedFilter(null)
    setSearch('')
  }

  const setSelectedValues = (key: FilterKey, values: string[]) => {
    searchParams.delete(key)
    for (const v of values) searchParams.append(key, v)
    searchParams.delete('page')
    setSearchParams(searchParams)
  }

  const clearAllFilters = () => {
    for (const k of FILTER_KEYS) searchParams.delete(k)
    searchParams.delete('page')
    setSearchParams(searchParams)
  }

  return (
    // eslint-disable-next-line tailwindcss/migration-from-tailwind-2
    <div className="h-96 w-80 overflow-clip">
      <div
        className="flex h-full w-[40rem]"
        style={{
          transform: selectedFilter ? 'translateX(-50%)' : 'translateX(0)',
          transition: 'transform 0.2s ease-in-out'
        }}
      >
        <div className="flex w-80 shrink-0 flex-col">
          <div className="flex h-12 shrink-0 items-center gap-2 border-b border-neutral-200 px-4 py-3">
            <FilterIcon className="text-neutral-700" />
            <span className="text-sm font-medium">
              {t('orders.filters_title')}
            </span>
            {activeCount > 0 && <Badge variant="count">{activeCount}</Badge>}
            {activeCount > 0 && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="ml-auto cursor-pointer text-xs font-medium text-neutral-600 underline-offset-4 transition-all hover:underline"
              >
                {t('orders.clear_all')}
              </button>
            )}
          </div>
          <div className="shrink-0 border-y border-solid border-neutral-200 px-4 py-3">
            <Input
              type="search"
              placeholder={t('orders.filters_search_placeholder', {
                label: selectedFilter
                  ? t(`orders.filter_categories.${selectedFilter}`)
                  : t('orders.filters_all')
              })}
              prefix={<MagnifyingGlassIcon className="size-5" />}
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              clear={search.length > 0}
              onClear={() => setSearch('')}
            />
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto py-2">
            {filters
              .filter(({ key }) =>
                t(`orders.filter_categories.${key}`)
                  .toLowerCase()
                  .includes(search.toLowerCase())
              )
              .map(({ key }) => {
                const categoryCount = searchParams.getAll(key).length
                return (
                  <li key={key} className="px-2">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between rounded-md px-3 py-2.5 font-roobert text-sm font-medium text-neutral-800 hover:bg-neutral-100"
                      onClick={() => setSelectedFilter(key)}
                    >
                      {t(`orders.filter_categories.${key}`)}
                      <span className="flex items-center gap-2">
                        {categoryCount > 0 && (
                          <Badge variant="count">{categoryCount}</Badge>
                        )}
                        <ChevronRight />
                      </span>
                    </button>
                  </li>
                )
              })}
          </ul>
        </div>
        <div className="flex w-80 shrink-0 flex-col">
          <div className="flex h-12 shrink-0 items-center gap-3 border-b border-neutral-200 px-4 py-3">
            <button
              onClick={handleBack}
              className="relative before:absolute before:inset-0 before:block before:scale-150"
            >
              <svg
                width="14px"
                height="14px"
                viewBox="0 0 24 24"
                stroke-width="2"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                color="currentColor"
              >
                <path
                  d="M21 12L3 12M3 12L11.5 3.5M3 12L11.5 20.5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
            </button>
            <span className="text-sm font-medium">
              {selectedFilter &&
                t(`orders.filter_categories.${selectedFilter}`)}
            </span>
          </div>
          <div className="shrink-0 border-y border-solid border-neutral-200 px-4 py-3">
            <Input
              type="search"
              placeholder={t('orders.filters_search_placeholder', {
                label: selectedFilter
                  ? t(
                      `orders.filter_categories.${selectedFilter}`
                    ).toLowerCase()
                  : ''
              })}
              prefix={<MagnifyingGlassIcon className="size-5" />}
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              clear={search.length > 0}
              onClear={() => setSearch('')}
            />
          </div>
          {activeFilter?.isLoading && (
            <div className="flex min-h-0 flex-1 items-center justify-center p-2">
              <Spinner label={t('orders.loading_filter_options')} />
            </div>
          )}
          {activeFilter &&
            !activeFilter.isLoading &&
            (activeFilter.options.length > 0 ? (
              <Checklist
                options={activeFilter.options}
                query={search}
                value={searchParams.getAll(activeFilter.key)}
                onValueChange={(next) =>
                  setSelectedValues(activeFilter.key, next)
                }
                labels={checklistLabels}
                // Country names come lower-cased.
                optionClassName="capitalize"
              />
            ) : (
              <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1 p-4 text-center">
                <span className="font-roobert text-sm font-medium text-neutral-800">
                  {t('filter.no_options_title')}
                </span>
                <span className="font-roobert text-xs text-neutral-500">
                  {t('filter.no_options_description', {
                    filter: t(
                      `orders.filter_categories.${activeFilter.key}`
                    ).toLowerCase()
                  })}
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

const ChevronRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="text-neutral-400"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
)
